<!--
  Command Center view "Opportunity" (was "Prospect"; stored value still "connect", saved board still "prospect"): the connectGS command center in swiftApp.

  WHAT IT SHOWS, AND WHERE EACH NUMBER COMES FROM
  - Contact Pipeline: columns the user builds here. A column is a name plus contact filters
    (helpers/connect/boardColumns.js): "+ Status column" adds a one-status column, "+ Custom column"
    opens the editor for any GSDesign contact filters (type, tags, source, dates…). Default: Pre-Approved /
    Waiting on Docs / In Contract, the CGS2022 command center lists. Saved to the account
    (helpers/connect/boardStore.js, board "prospect"; browser copy as fallback).
  - "View as": every GSDesignContacts call carries X-GSD-Act-As, so the server shows the viewed person's
    contacts and columns under GSDesign's impersonation rules; column changes aren't saved while viewing as.
    Each column is one GSDesignContacts Query (status is X, scope mine, oldest update first),
    so the total is the server's count, not the number of cards drawn.
  - "N not touched in 30+ days": a second Query per column with "Last updated on or before
    <today − 30 days>". It's the server's count of the same rule the card chips use
    (contactRules.staleBand), so the figure and the red chips can't disagree.
  - Moving a card into a one-status column is a status change (custom columns don't take drops): the contact is read fresh (Get) and saved
    with only its status changed (Save), then both columns reload. Nothing moves on screen before
    the server agrees.
  - Tasks / Birthdays: the CGS2022 tasksCard and birthdayCard endpoints, unchanged. Shown as the My Day tiles;
    clicking a tile opens a drawer from the right with that list (birthdays can be ticked off there).

  NOT HERE YET
  - The CGS saved opportunity lists ((Refi)nder, IRIS, Mudloff leads). They are per-user saved
    dashboard queries on older endpoints; they come in a follow-up.
-->
<template>
  <div class="cx cxd">
    <section class="cxd-groups" aria-label="Today at a glance">
      <div class="cx-grp cxd-grp-pipe">
        <div class="cx-grp-t"><i class="fa-duotone fa-solid fa-address-card" aria-hidden="true"></i> Contact Pipeline</div>
        <div class="cx-tiles" :style="{ gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, minmax(0, 1fr))` }">
          <button v-for="col in columns" :key="col.id" type="button" class="cx-tile" @click="openColumn(col)">
            <div class="cx-tile-l">{{ col.title }}</div>
            <div class="cx-tile-v">{{ col.loading ? '…' : col.total }}</div>
          </button>
        </div>
        <div class="cx-foot">
          <span :class="{ 'cxd-warn': staleTotal > 0 }">{{ staleSummary }}</span>
          <span>{{ updatedLabel }}</span>
        </div>
      </div>
      <div class="cx-grp">
        <div class="cx-grp-t"><i class="fa-duotone fa-solid fa-list-check" aria-hidden="true"></i> My Day</div>
        <div class="cx-tiles" style="grid-template-columns: repeat(3, minmax(0, 1fr));">
          <button type="button" class="cx-tile" :aria-expanded="drawer === 'today' ? 'true' : 'false'" @click="openDrawer('today')"><div class="cx-tile-l">Tasks due today</div><div class="cx-tile-v">{{ tasksLoading ? '…' : dueToday.length }}</div></button>
          <button type="button" class="cx-tile" :aria-expanded="drawer === 'overdue' ? 'true' : 'false'" @click="openDrawer('overdue')"><div class="cx-tile-l">Overdue tasks</div><div class="cx-tile-v" :class="{ 'cxd-red': overdue.length }">{{ tasksLoading ? '…' : overdue.length }}</div></button>
          <button type="button" class="cx-tile" :aria-expanded="drawer === 'birthdays' ? 'true' : 'false'" @click="openDrawer('birthdays')"><div class="cx-tile-l">Birthdays</div><div class="cx-tile-v">{{ birthdaysLoading ? '…' : birthdays.length }}</div></button>
        </div>
        <div class="cx-foot"><span></span><span>{{ updatedLabel }}</span></div>
      </div>
    </section>

    <div class="cxd-main">
      <section class="cx-card cx-card-pad cxd-board" aria-label="Contact pipeline">
        <div class="cx-head">
          <h2 class="cx-h2">Contact Pipeline</h2>
          <div style="display: flex; gap: 8px;">
            <button type="button" class="cx-btn cx-btn--sm" :disabled="anyLoading" @click="load">
              <i class="fa-light fa-arrows-rotate" aria-hidden="true"></i> Refresh
            </button>
            <label v-if="availableStatuses.length" class="cxd-addcol">
              <span class="cxd-sr">Add a status column</span>
              <select class="cx-select" :value="''" @change="addColumn($event.target.value); $event.target.value = ''">
                <option value="" disabled>+ Status column</option>
                <option v-for="st in availableStatuses" :key="st" :value="st">{{ st }}</option>
              </select>
            </label>
            <button type="button" class="cx-btn cx-btn--sm" :disabled="!editorFields.length" @click="editing = 'new'">+ Custom column</button>
            <router-link class="cx-btn cx-btn--sm" :to="{ name: 'contacts' }">Open Contacts</router-link>
          </div>
        </div>
        <div v-if="optionsError" class="cx-banner cx-banner--error" role="alert"><i class="fa-solid fa-circle-exclamation"></i> {{ optionsError }}</div>
        <div v-if="moveError" class="cx-banner cx-banner--error" role="alert">{{ moveError }}</div>
        <div v-if="boardNote" class="cx-banner cx-banner--warn" role="status"><i class="fa-light fa-circle-info" aria-hidden="true"></i> <span>{{ boardNote }}</span></div>
        <div class="cx-board">
          <section v-for="(col, i) in columns" :key="col.id" class="cx-col" :class="{ drop: dropTarget === col.id }" :aria-label="col.title"
                   @dragover.prevent="dropTarget = col.id" @dragleave="dropTarget === col.id && (dropTarget = '')" @drop.prevent="onDrop(col)">
            <div class="cx-colh">
              <span class="cx-dot" :style="{ background: colorFor(i) }"></span>
              <span style="flex: 1; min-width: 0;">{{ col.title }}</span>
              <span class="cx-muted">{{ col.loading ? '' : col.total }}</span>
              <span class="cxd-colbtns">
                <button type="button" :aria-label="`Edit ${col.title}`" title="Edit column" @click="editing = col"><i class="fa-light fa-pen" aria-hidden="true"></i></button>
                <button type="button" :disabled="i === 0" :aria-label="`Move ${col.title} left`" title="Move left" @click="moveColumn(i, -1)">‹</button>
                <button type="button" :disabled="i === columns.length - 1" :aria-label="`Move ${col.title} right`" title="Move right" @click="moveColumn(i, 1)">›</button>
                <button v-if="columns.length > 1" type="button" :aria-label="`Remove column ${col.title}`" title="Remove column" @click="removeColumn(col.id)">×</button>
              </span>
            </div>
            <div v-if="!singleStatusOf(col) && describe(col)" class="cx-small cx-muted cxd-rule" :title="describe(col)">{{ describe(col) }}</div>
            <div v-if="col.loading" class="cx-skel" aria-hidden="true"><span style="width: 90%"></span><span style="width: 70%"></span><span style="width: 80%"></span></div>
            <div v-else-if="col.error" class="cx-banner cx-banner--error" role="alert">{{ col.error }}</div>
            <p v-else-if="!col.rows.length" class="cx-small cx-muted" style="margin: 4px;">No contacts.</p>
            <router-link v-for="r in col.rows" :key="r.contact_id" class="cx-kc" :class="{ busy: moving === r.contact_id }"
                         :to="{ name: 'contact', params: { id: r.contact_id } }" draggable="true" @dragstart="onDragStart(r, col)">
              <span :style="{ background: colorFor(i) }"></span>
              <span class="cx-kb">
                <span class="cx-kn">{{ nameOf(r) }}</span>
                <span v-if="r.email" class="cx-km">{{ r.email }}</span>
                <span class="cx-kr">
                  <span v-if="r.type" class="cx-pill" :class="typeClassOf(r.type)">{{ r.type }}</span>
                  <span class="cx-pill" :class="`cx-pill--${band(r)}`">{{ staleText(r) }}</span>
                </span>
              </span>
            </router-link>
            <router-link v-if="!col.loading && col.total > col.rows.length" :to="contactsLinkFor(col)" class="cx-small" style="padding: 4px;">
              Show {{ col.total - col.rows.length }} more
            </router-link>
          </section>
        </div>
        <p class="cx-small cx-muted" style="margin: 8px 2px 0;">{{ whereLabel(boardWhere) }} Oldest update first. Drag a card to another column to change its status. Chips: green under 7 days, amber under 30, red 30+.</p>
      </section>

    </div>
    <!-- My Day drawer: the tiles above open it from the right (replaces the old Tasks / Birthdays side cards). -->
    <transition name="cxd-fade"><div v-if="drawer" class="cxd-veil" @click="drawer = ''"></div></transition>
    <transition name="cxd-slide">
      <aside v-if="drawer" class="cxd-drawer cx-card" role="dialog" aria-modal="true" :aria-label="drawerTitle">
        <header class="cxd-dh">
          <div>
            <h2 class="cx-h2">{{ drawerTitle }}</h2>
            <span class="cx-small cx-muted">{{ drawerCount }}</span>
          </div>
          <button ref="drawerClose" type="button" class="cxd-dx" aria-label="Close" @click="drawer = ''">×</button>
        </header>
        <div class="cxd-db">
          <template v-if="drawer === 'birthdays'">
            <div v-if="birthdaysLoading" class="cx-skel" aria-hidden="true"><span></span><span style="width: 60%"></span></div>
            <div v-else-if="birthdaysError" class="cx-banner cx-banner--error" role="alert">{{ birthdaysError }}</div>
            <p v-else-if="!birthdays.length" class="cx-small cx-muted" style="margin: 0;">No birthdays coming up.</p>
            <div v-else class="cx-rows">
              <label v-for="b in birthdays" :key="b.guid" class="cx-row" :class="{ 'cxd-busy': b._busy }">
                <span class="cx-av">{{ initialsOf(`${b.first_name || ''} ${b.last_name || ''}`) }}</span>
                <span style="flex: 1; min-width: 0;">
                  <span style="display: block; font-size: 14px; font-weight: 600;">{{ b.first_name }} {{ b.last_name }}</span>
                  <span class="cx-small cx-muted">{{ fmtDate(b.birthday, { month: 'short', day: 'numeric' }) }}</span>
                </span>
                <input type="checkbox" class="cx-check" :disabled="b._busy" :aria-label="`Mark ${b.first_name} ${b.last_name} done`" @change="acknowledge(b)" />
              </label>
            </div>
          </template>
          <template v-else>
            <div v-if="tasksLoading" class="cx-skel" aria-hidden="true"><span></span><span style="width: 70%"></span></div>
            <div v-else-if="tasksError" class="cx-banner cx-banner--error" role="alert">{{ tasksError }}</div>
            <p v-else-if="!drawerTasks.length" class="cx-small cx-muted" style="margin: 0;">{{ drawer === 'today' ? 'Nothing due today.' : 'No overdue tasks.' }}</p>
            <div v-else class="cx-rows">
              <label v-for="t in drawerTasks" :key="t.task_id" class="cx-row" :class="{ 'cxd-busy': completing.includes(t.task_id) }">
                <input type="checkbox" class="cx-check" :disabled="completing.includes(t.task_id)" :aria-label="`Complete ${t.subject || 'task'}`" @change="completeTask(t)" />
                <span style="flex: 1; min-width: 0;">
                  <span style="display: block; font-size: 14px; font-weight: 600;">{{ t.subject || 'Task' }}</span>
                  <span v-if="taskContact(t)" class="cx-small cx-muted">{{ taskContact(t) }}</span>
                </span>
                <span v-if="t.due_date" class="cx-small" :class="{ 'cxd-red': isOverdue(t) }">{{ fmtDate(t.due_date) }}</span>
              </label>
            </div>
          </template>
        </div>
      </aside>
    </transition>

    <BoardColumnEditor
      v-if="editing"
      :column="editing === 'new' ? null : editing"
      :fields="editorFields"
      :ops-by-kind="CONTACT_OPS"
      :extra-choices="{ status: statuses, type: types }"
      noun="contacts"
      singular="contact"
      @save="onSave"
      @remove="onRemove"
      @cancel="editing = null"
    />
  </div>
</template>

<script>
import '@/assets/css/connect.css'
import api from '@/api/connect.service.js'
import BoardColumnEditor from '@/components/connect/BoardColumnEditor.vue'
import {
  cleanColumn, statusColumn, singleStatusOf, describeFilters, CONTACT_OPS
} from '@/helpers/connect/boardColumns.js'
import { loadBoard, saveBoard, whereLabel } from '@/helpers/connect/boardStore.js'
import {
  loadPipelineStatuses, DEFAULT_PIPELINE_STATUSES, columnColor, displayName, initials, typeClass, daysSince, staleBand, staleLabel, fmtDate
} from '@/helpers/connect/contactRules.js'

const CARDS_PER_COLUMN = 6

function isoDay(d) {
  const p = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export default {
  name: 'ConnectDashboard',
  components: { BoardColumnEditor },
  data() {
    return {
      CONTACT_OPS,
      columns: [],
      statuses: [],
      types: [],
      contactFields: [],
      editing: null,
      drawer: '', // '' | 'today' | 'overdue' | 'birthdays'
      boardWhere: '',
      boardNote: '',
      cutoff: '',
      dragged: null,
      dropTarget: '',
      moving: '',
      moveError: '',
      optionsError: '',
      tasks: [],
      tasksLoading: true,
      tasksError: '',
      completing: [], // task_ids being marked done
      birthdays: [],
      birthdaysLoading: true,
      birthdaysError: '',
      loadedAt: null,
      now: Date.now(),
      seq: 0
    }
  },
  computed: {
    availableStatuses() { return this.statuses.filter(s => !this.columns.some(c => singleStatusOf(c) === s)) },
    // The editor offers GSDesign's contact filter fields, minus marketing ("In a campaign").
    editorFields() { return this.contactFields.filter(f => f && f.key && f.key !== 'in_campaign') },
    anyLoading() { return this.columns.some(c => c.loading) || this.tasksLoading || this.birthdaysLoading },
    staleTotal() { return this.columns.reduce((n, c) => n + (c.stale || 0), 0) },
    staleSummary() {
      if (this.columns.some(c => c.loading)) return ''
      if (this.columns.some(c => c.staleError)) return "Couldn't count stale contacts"
      return this.staleTotal ? `${this.staleTotal} not touched in 30+ days` : 'Everything updated in the last 30 days'
    },
    updatedLabel() {
      if (!this.loadedAt) return ''
      const mins = Math.floor((this.now - this.loadedAt) / 60000)
      return mins < 1 ? 'Updated just now' : `Updated ${mins}m ago`
    },
    openTasks() { return this.tasks.filter(t => String(t.status || '').toLowerCase() !== 'completed') },
    dueToday() {
      const today = new Date(this.now)
      return this.openTasks.filter(t => t.due_date && sameDay(new Date(t.due_date), today))
    },
    overdue() { return this.openTasks.filter(t => this.isOverdue(t)) },
    // Drawer: due today, or overdue oldest first.
    drawerTasks() {
      const byDue = (a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
      return (this.drawer === 'today' ? this.dueToday : this.overdue).slice().sort(byDue)
    },
    drawerTitle() { return { today: 'Tasks due today', overdue: 'Overdue tasks', birthdays: 'Birthdays' }[this.drawer] || '' },
    drawerCount() {
      if (this.drawer === 'birthdays') return this.birthdaysLoading ? '' : `${this.birthdays.length} coming up`
      return this.tasksLoading ? '' : `${this.drawerTasks.length} task${this.drawerTasks.length === 1 ? '' : 's'}`
    }
  },
  mounted() {
    this.onKey = e => { if (e.key === 'Escape' && this.drawer && !this.editing) this.drawer = '' }
    window.addEventListener('keydown', this.onKey)
    this.load()
    this.clock = setInterval(() => { this.now = Date.now() }, 60000)
  },
  beforeUnmount() {
    clearInterval(this.clock)
    window.removeEventListener('keydown', this.onKey)
  },
  methods: {
    colorFor: columnColor,
    nameOf: displayName,
    initialsOf: initials,
    typeClassOf: typeClass,
    fmtDate,
    whereLabel,
    band(r) { return staleBand(daysSince(r.updated, this.now)) },
    staleText(r) { return staleLabel(daysSince(r.updated, this.now)) },
    isOverdue(t) {
      if (!t.due_date) return false
      const due = new Date(t.due_date)
      const today = new Date(this.now)
      return due < today && !sameDay(due, today)
    },
    taskContact(t) {
      const c = Array.isArray(t.contacts) && t.contacts[0]
      return c ? [c.first_name, c.last_name].filter(Boolean).join(' ') : ''
    },
    singleStatusOf,
    openDrawer(which) {
      this.drawer = this.drawer === which ? '' : which
      if (this.drawer) this.$nextTick(() => this.$refs.drawerClose?.focus())
    },
    describe(col) { return describeFilters(col.filters, this.editorFields, CONTACT_OPS) },
    // The Contacts table with this column's filters: ?status= for a one-status column, else ?filters=.
    contactsLinkFor(col) {
      const status = singleStatusOf(col)
      return status ? { name: 'contacts', query: { status } } : { name: 'contacts', query: { filters: JSON.stringify(col.filters) } }
    },
    openColumn(col) { this.$router.push(this.contactsLinkFor(col)) },

    // ── Board columns: added on the right, moved with the arrows, removed with ×; kept per browser ──
    newColumn(def) { return { ...cleanColumn(def), rows: [], total: 0, stale: 0, loading: true, error: '', staleError: false } },
    async saveColumns() {
      const r = await saveBoard('prospect', this.columns)
      this.boardWhere = r.where
      this.boardNote = r.note
    },
    addColumn(status) {
      if (!status || this.columns.some(c => singleStatusOf(c) === status)) return
      this.columns.push(this.newColumn(statusColumn(status)))
      this.saveColumns()
      this.loadColumn(this.columns[this.columns.length - 1], this.cutoff || isoDay(new Date(Date.now() - 30 * 86400000)), this.seq)
    },
    removeColumn(id) {
      if (this.columns.length <= 1) return
      this.columns = this.columns.filter(c => c.id !== id)
      this.saveColumns()
    },
    onSave(def) {
      const i = this.columns.findIndex(c => c.id === def.id)
      if (i >= 0) this.columns.splice(i, 1, this.newColumn(def))
      else this.columns.push(this.newColumn(def))
      this.editing = null
      this.saveColumns()
      this.loadColumn(this.columns.find(c => c.id === def.id), this.cutoff || isoDay(new Date(Date.now() - 30 * 86400000)), this.seq)
    },
    onRemove(id) {
      this.editing = null
      if (this.columns.length <= 1) return
      this.removeColumn(id)
    },
    moveColumn(i, delta) {
      const j = i + delta
      if (j < 0 || j >= this.columns.length) return
      const next = this.columns.slice()
      const [c] = next.splice(i, 1)
      next.splice(j, 0, c)
      this.columns = next
      this.saveColumns()
    },
    onDragStart(r, col) { this.dragged = { id: r.contact_id, from: col.id } },
    async onDrop(col) {
      this.dropTarget = ''
      const d = this.dragged
      this.dragged = null
      if (!d || d.from === col.id || this.moving) return
      const status = singleStatusOf(col)
      if (!status) {
        this.moveError = `"${col.title}" is a custom column, so dropping a card there can't set a status. Drop it on a one-status column.`
        return
      }
      this.moving = d.id
      this.moveError = ''
      try {
        const fresh = await api.getContact(d.id)
        if (!fresh?.can_edit) throw new Error("You can't edit this contact")
        await api.saveContact({ ...fresh.contact, status })
        const from = this.columns.find(c => c.id === d.from)
        const cutoff = this.cutoff || isoDay(new Date(Date.now() - 30 * 86400000))
        await Promise.all([from && this.loadColumn(from, cutoff, this.seq), this.loadColumn(col, cutoff, this.seq)].filter(Boolean))
      } catch (e) {
        this.moveError = `Couldn't change the status: ${e.message}`
      } finally {
        this.moving = ''
      }
    },

    async load() {
      const seq = ++this.seq
      this.now = Date.now()
      this.loadTasks(seq)
      this.loadBirthdays(seq)
      this.optionsError = ''
      let statuses = []
      try {
        const options = await api.contactOptions()
        statuses = options?.statuses || []
        this.statuses = statuses
        this.types = options?.types || []
        this.contactFields = options?.fields || []
      } catch (e) {
        // Without Options the default columns still load; only the column picker is affected.
        this.optionsError = `Couldn't load contact statuses: ${e.message}`
      }
      if (seq !== this.seq) return
      // Saved board columns (account, else browser), else the status columns picked before custom columns
      // existed, else the defaults. While viewing as someone, never this browser's picks: the plain defaults.
      const stored = await loadBoard('prospect')
      if (seq !== this.seq) return
      this.boardWhere = stored.where
      this.boardNote = stored.note
      const fallback = stored.viewingAs
        ? DEFAULT_PIPELINE_STATUSES.filter(st => !statuses.length || statuses.includes(st))
        : loadPipelineStatuses(statuses)
      this.columns = (stored.columns && stored.columns.length ? stored.columns : fallback.map(statusColumn)).map(def => this.newColumn(def))
      if (!stored.columns && !stored.viewingAs) this.saveColumns()
      const cutoff = isoDay(new Date(this.now - 30 * 86400000))
      this.cutoff = cutoff
      await Promise.all(this.columns.map(col => this.loadColumn(col, cutoff, seq)))
      if (seq === this.seq) this.loadedAt = Date.now()
    },

    async loadColumn(col, cutoff, seq) {
      const base = { list: 'all', scope: 'mine', search: '', page: 1 }
      const filters = (col.filters || []).map(f => ({ ...f }))
      const [cards, stale] = await Promise.allSettled([
        api.queryContacts({ ...base, filters, sort: 'updated', sort_dir: 'asc', page_size: CARDS_PER_COLUMN }),
        api.queryContacts({ ...base, filters: [...filters, { field: 'updated', op: 'on_or_before', value: cutoff }], page_size: 1 })
      ])
      if (seq !== this.seq) return
      if (cards.status === 'fulfilled') {
        col.rows = cards.value?.rows || []
        col.total = cards.value?.total ?? col.rows.length
      } else {
        col.error = cards.reason?.message || 'Please try again'
      }
      if (stale.status === 'fulfilled') col.stale = stale.value?.total || 0
      else col.staleError = true
      col.loading = false
    },

    async loadTasks(seq) {
      this.tasksLoading = true
      this.tasksError = ''
      try {
        const tasks = await api.getMyTasks()
        if (seq === this.seq) this.tasks = Array.isArray(tasks) ? tasks : []
      } catch (e) {
        if (seq === this.seq) this.tasksError = e.message
      } finally {
        if (seq === this.seq) this.tasksLoading = false
      }
    },

    async loadBirthdays(seq) {
      this.birthdaysLoading = true
      this.birthdaysError = ''
      try {
        const list = await api.getImminentBirthdays()
        if (seq === this.seq) this.birthdays = (Array.isArray(list) ? list : []).map(b => ({ ...b, _busy: false }))
      } catch (e) {
        if (seq === this.seq) this.birthdaysError = e.message
      } finally {
        if (seq === this.seq) this.birthdaysLoading = false
      }
    },

    // Checking a task off completes it (same call as CGS2022's tasks card) and drops it from the tiles.
    async completeTask(t) {
      this.completing.push(t.task_id)
      try {
        await api.completeTask(t)
        this.tasks = this.tasks.filter(x => x.task_id !== t.task_id)
      } catch (e) {
        this.tasksError = `Couldn't complete the task: ${e.message}`
      } finally {
        this.completing = this.completing.filter(id => id !== t.task_id)
      }
    },

    async acknowledge(b) {
      b._busy = true
      try {
        await api.acknowledgeBirthday(b.guid)
        this.birthdays = this.birthdays.filter(x => x.guid !== b.guid)
      } catch (e) {
        b._busy = false
        this.birthdaysError = `Couldn't mark it done: ${e.message}`
      }
    }
  }
}
</script>

<style scoped>
.cxd { display: flex; flex-direction: column; gap: 14px; width: 100%; }
.cxd-groups { display: flex; flex-wrap: wrap; gap: 12px; }
.cxd-groups > .cx-grp { flex: 1 1 320px; }
.cxd-groups > .cxd-grp-pipe { flex: 1.4 1 420px; }
.cxd-main { display: flex; gap: 14px; align-items: flex-start; }
.cxd-board { flex: 1 1 auto; min-width: 0; }
/* My Day tiles are buttons that open the drawer. */
button.cx-tile { font: inherit; color: inherit; text-align: left; cursor: pointer; width: 100%; }
button.cx-tile:hover { border-color: #cfd5dd; box-shadow: 0 2px 6px rgba(16, 24, 40, .08); }
button.cx-tile[aria-expanded="true"] { border-color: var(--cx-accent, #398b87); box-shadow: 0 0 0 3px rgba(57, 139, 135, .15); }
.cxd-veil { position: fixed; inset: 0; z-index: 900; background: rgba(15, 23, 42, .25); }
.cxd-drawer { position: fixed; top: 0; right: 0; bottom: 0; z-index: 901; width: min(440px, 100vw); border-radius: 12px 0 0 12px; display: flex; flex-direction: column; box-shadow: -12px 0 32px rgba(16, 24, 40, .16); }
.cxd-dh { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; padding: 18px 20px 14px; border-bottom: 1px solid #eef0f3; }
.cxd-dx { border: 0; background: transparent; color: #6b7280; font-size: 22px; line-height: 1; width: 32px; height: 32px; border-radius: 8px; cursor: pointer; }
.cxd-dx:hover { background: #eef0f3; color: #111827; }
.cxd-db { flex: 1 1 auto; overflow: auto; padding: 14px 20px 20px; }
.cxd-slide-enter-active, .cxd-slide-leave-active { transition: transform .22s ease; }
.cxd-slide-enter-from, .cxd-slide-leave-to { transform: translateX(100%); }
.cxd-fade-enter-active, .cxd-fade-leave-active { transition: opacity .22s ease; }
.cxd-fade-enter-from, .cxd-fade-leave-to { opacity: 0; }
.cxd-warn { color: #a8261b; }
.cxd-red { color: #a8261b; font-weight: 600; }
.cxd-busy { opacity: .5; }
.cxd-rule { margin: -4px 4px 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cxd-addcol .cx-select { height: 30px; border-radius: 999px; padding: 0 10px; }
.cxd-colbtns { display: inline-flex; gap: 0; margin-left: 2px; }
.cxd-colbtns button { border: 0; background: transparent; color: #6b7280; width: 22px; height: 22px; border-radius: 6px; font-size: 15px; line-height: 1; cursor: pointer; padding: 0; }
.cxd-colbtns button:hover:not(:disabled) { background: #e2e5ea; color: #111827; }
.cxd-colbtns button:disabled { opacity: .3; cursor: default; }
.cxd-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
</style>
